import { Button } from '@/components/ui/Button';
// Ancien modal/abonnement retiré
import {
  AutomationConfiguration,
  AutomationExecution,
  Folder,
} from '@/types/domain.types';
import { EnvoiDocument } from '@/types/envoi.types';
import {
  Calendar,
  CheckCircle2,
  CircleAlert,
  CirclePause,
  Clock,
  Play,
  RefreshCw,
  Settings,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getTemplateDisplayName } from '../constants/automation.constants';
import {
  AUTOMATION_EXECUTION_TONE_CLASSES,
  getAutomationExecutionPresentation,
  readDsnAcceptanceSummary,
  type AutomationExecutionIcon,
} from '../utils/automation-execution-presentation';
import { AutresEnvois, EnvoisDeLExecution } from './EnvoisDocuments';
import { DsnReturnDetails } from './DsnReturnDetails';
import { DsnDeclarationMonitor } from './DsnDeclarationMonitor';

interface AutomationHistoryProps {
  configurations: AutomationConfiguration[];
  executions: AutomationExecution[];
  folder?: Folder | undefined | null; // Add folder prop for automation drawer
  onAutomationClick?: (folder: Folder) => void; // Add callback for automation click
  /**
   * Ce qui est réellement parti par courriel pour ce dossier (01/09/2026) —
   * une entrée par destinataire, rangée sous l'exécution qui l'a produite.
   */
  envois?: EnvoiDocument[];
  envoisIndisponibles?: boolean;
  onRechargerEnvois?: () => void;
  /** Le verrou métier ne couvre que la configuration, jamais la consultation. */
  configurationGuard?: ComponentType<{ children: ReactNode }>;
}

export function AutomationHistory({
  configurations,
  executions,
  folder,
  onAutomationClick,
  envois = [],
  envoisIndisponibles = false,
  onRechargerEnvois,
  configurationGuard,
}: AutomationHistoryProps) {
  const { t } = useTranslation();

  // Un seul appel pour tout l'onglet : on range ici, sans requête par
  // exécution. Ce qui ne se rattache à aucune exécution affichée — un envoi
  // lancé à la main, ou une exécution plus ancienne — va dans « Autres envois ».
  const envoisParExecution = new Map<string, EnvoiDocument[]>();
  const executionsAffichees = new Set(
    executions.map(execution => execution.uuid)
  );
  const autresEnvois: EnvoiDocument[] = [];
  for (const envoi of envois) {
    if (envoi.executionUuid && executionsAffichees.has(envoi.executionUuid)) {
      const groupe = envoisParExecution.get(envoi.executionUuid) ?? [];
      groupe.push(envoi);
      envoisParExecution.set(envoi.executionUuid, groupe);
    } else {
      autresEnvois.push(envoi);
    }
  }

  const getStatusIcon = (icon: AutomationExecutionIcon) => {
    switch (icon) {
      case 'success':
        return <CheckCircle2 aria-hidden="true" className="h-4 w-4" />;
      case 'failure':
        return <CircleAlert aria-hidden="true" className="h-4 w-4" />;
      case 'running':
        return <Play aria-hidden="true" className="h-4 w-4" />;
      case 'retry':
        return <RefreshCw aria-hidden="true" className="h-4 w-4" />;
      case 'paused':
        return <CirclePause aria-hidden="true" className="h-4 w-4" />;
      case 'pending':
      default:
        return <Clock aria-hidden="true" className="h-4 w-4" />;
    }
  };

  const handleAutomationClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (!folder || !onAutomationClick) return;

    // Ancien abonnement retiré

    onAutomationClick(folder);
  };

  const configurationsActives = (
    <div>
      <h3 className="mb-4 text-lg font-semibold text-neutral-900">
        {t('automation.activeConfigurations', 'Configurations actives')}
      </h3>
      <div className="space-y-4">
        {configurations.length > 0 ? (
          configurations.map(config => (
            <div
              key={config.uuid}
              className="rounded-lg border border-neutral-200 bg-white p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-primary-50 flex h-8 w-8 items-center justify-center rounded-lg">
                    <Calendar className="text-primary-600 h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">
                      {getTemplateDisplayName(config.templateId)}{' '}
                      {config.templateId === 'generate-edition' && (
                        <span className="text-sm text-neutral-600">
                          ({config.editionOptionName})
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-neutral-600">
                      {t('automation.dayOfMonth', 'Jour du mois')}:{' '}
                      {config.dayOfMonth}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                      config.isActive
                        ? 'text-success-600 bg-success-50'
                        : 'bg-neutral-50 text-neutral-600'
                    }`}
                  >
                    {config.isActive
                      ? t('common.active', 'Actif')
                      : t('common.inactive', 'Inactif')}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAutomationClick}
                    disabled={!folder || !onAutomationClick}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    {t('common.edit', 'Modifier')}
                  </Button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-neutral-500">
                    {t('automation.notificationEmail', 'Email de notification')}
                    :
                  </p>
                  <p className="font-medium text-neutral-900">
                    {config.notificationEmail}
                  </p>
                </div>
                <div>
                  <p className="text-neutral-500">
                    {t('automation.sendEmail', "Email d'envoi")}:
                  </p>
                  <p className="font-medium text-neutral-900">
                    {config.templateId === 'generate-edition'
                      ? config.editionEmail
                      : config.templateId === 'send-bulletin'
                        ? config.email
                        : config.dsnEmail}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-6 text-center">
            <Calendar className="mx-auto mb-2 h-8 w-8 text-neutral-400" />
            <p className="text-sm text-neutral-600">
              {t(
                'automation.noConfigurations',
                "Aucune configuration d'automatisation"
              )}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={handleAutomationClick}
              disabled={!folder || !onAutomationClick}
            >
              <Settings className="mr-2 h-4 w-4" />
              {t('automation.configure.title', 'Configurer')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const ConfigurationGuard = configurationGuard;

  return (
    <div className="space-y-6">
      {folder?.uuid && <DsnDeclarationMonitor folderUuid={folder.uuid} />}

      {ConfigurationGuard ? (
        <ConfigurationGuard>{configurationsActives}</ConfigurationGuard>
      ) : (
        configurationsActives
      )}

      {/* Recent Executions */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-neutral-900">
          {t('automation.recentExecutions', 'Exécutions récentes')}
        </h3>
        <div className="space-y-4">
          {executions.length > 0 ? (
            executions.map(execution => {
              const presentation =
                getAutomationExecutionPresentation(execution);
              const cancellation = presentation.cancellation;
              const requiresAttention =
                presentation.requiresAttention === true ||
                presentation.unknownCancellation ||
                cancellation?.requiresAttention === true;
              const toneClasses =
                AUTOMATION_EXECUTION_TONE_CLASSES[presentation.tone];
              const dsnAcceptance = readDsnAcceptanceSummary(execution);

              return (
                <div
                  key={execution.uuid}
                  className="rounded-lg border border-neutral-200 bg-white p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${toneClasses}`}
                      >
                        {getStatusIcon(presentation.icon)}
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900">
                          {t('automation.execution', 'Exécution')} -{' '}
                          {getTemplateDisplayName(execution.templateId)}
                        </p>
                        <p className="text-sm text-neutral-600">
                          {new Date(execution.scheduledAt).toLocaleDateString(
                            'fr-FR',
                            {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            }
                          )}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${toneClasses}`}
                    >
                      {t(presentation.labelKey, presentation.labelFallback)}
                    </span>
                  </div>

                  {presentation.descriptionKey &&
                    presentation.descriptionFallback && (
                      <div
                        role={requiresAttention ? 'alert' : 'status'}
                        className={`mt-3 rounded-lg border px-3 py-2 ${toneClasses}`}
                      >
                        <p className="text-sm">
                          {t(
                            presentation.descriptionKey,
                            presentation.descriptionFallback
                          )}
                        </p>
                        {(cancellation || presentation.unknownCancellation) && (
                          <div className="mt-2 flex flex-wrap gap-2 text-xs font-medium">
                            {cancellation?.retryPlanned && (
                              <span className="rounded-full border border-current px-2 py-0.5">
                                {t(
                                  'automation.cancellation.retryPlanned',
                                  'Nouvelle tentative automatique prévue'
                                )}
                              </span>
                            )}
                            <span className="rounded-full border border-current px-2 py-0.5">
                              {requiresAttention
                                ? t(
                                    'automation.cancellation.attentionRequired',
                                    'Action requise'
                                  )
                                : t(
                                    'automation.cancellation.noActionRequired',
                                    'Aucune action requise'
                                  )}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                  {dsnAcceptance &&
                    (dsnAcceptance.availableReports > 0 ||
                      dsnAcceptance.status === 'rejected' ||
                      dsnAcceptance.status === 'accepted_with_anomalies') && (
                      <DsnReturnDetails
                        executionUuid={execution.uuid}
                        summary={dsnAcceptance}
                      />
                    )}

                  {execution.error && execution.status !== 'cancelled' && (
                    <div className="bg-error-50 mt-3 rounded-lg p-3">
                      <p className="text-error-700 text-sm">
                        {execution.error}
                      </p>
                    </div>
                  )}
                  <EnvoisDeLExecution
                    envois={envoisParExecution.get(execution.uuid) ?? []}
                  />
                </div>
              );
            })
          ) : (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-6 text-center">
              <Clock className="mx-auto mb-2 h-8 w-8 text-neutral-400" />
              <p className="text-sm text-neutral-600">
                {t('automation.noExecutions', 'Aucune exécution récente')}
              </p>
            </div>
          )}
        </div>
        {envois.length > 0 && (
          <p className="mt-4 px-2 text-xs text-neutral-500">
            « Accepté » signifie que le service d’envoi a pris en charge le
            message ; cela ne prouve ni sa livraison finale ni son ouverture.
          </p>
        )}
        {envoisIndisponibles && (
          <div
            role="alert"
            className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-neutral-50 px-3 py-2"
          >
            <p className="text-sm text-neutral-700">
              Le détail des destinataires est momentanément indisponible.
            </p>
            {onRechargerEnvois && (
              <Button variant="outline" size="sm" onClick={onRechargerEnvois}>
                Réessayer
              </Button>
            )}
          </div>
        )}
      </div>

      <AutresEnvois envois={autresEnvois} />

      {/* Ancien modal d'abonnement retiré */}
    </div>
  );
}
